import { useEffect, useRef } from "react";
import { useTabsContext } from "../../context/TabsContext";
import { useId } from "react";

/**
 * @param {Object} props
 * @param {string} props.value - Unique identifier matching a TabPanel's value.
 * @param {boolean} [props.disabled]
 * @param {React.ReactNode} props.children
 */
const Tab = ({
  value,
  disabled = false,
  children,
  className = "",
  ...rest
}) => {
  const { activeValue, selectTab, orientation, registerTab, moveFocus } =
    useTabsContext("Tab");
    
  const ref = useRef(null);
  const id = useId();
  const isActive = activeValue === value;

  useEffect(() => {
    return registerTab(value, { disabled, ref });
  }, [value, disabled, registerTab]);

  const handleKeyDown = (e) => {
    const nextKey = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
    const prevKey = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";

    switch (e.key) {
      case nextKey:
        e.preventDefault();
        moveFocus(value, "next");
        break;
      case prevKey:
        e.preventDefault();
        moveFocus(value, "prev");
        break;
      case "Home":
        e.preventDefault();
        moveFocus(value, "first");
        break;
      case "End":
        e.preventDefault();
        moveFocus(value, "last");
        break;
      default:
        break;
    }
  };

  return (
    <button
      ref={ref}
      role="tab"
      type="button"
      id={`tab-${id}`}
      data-state={isActive ? "active" : "inactive"}
      aria-selected={isActive}
      aria-disabled={disabled || undefined}
      aria-controls={`panel-${value}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled || false}
      onClick={() => !disabled && selectTab(value)}
      onKeyDown={handleKeyDown}
      className={`tabs-tab${isActive ? " tabs-tab-active" : ""} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

export default Tab;
